<script setup lang="ts">
import { EButton, EDropdownMenu, type MenuItem } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const { t } = useI18n()

const initials = computed(() => {
  const name = auth.displayName.trim()
  if (name === '') return t('shell.userMenu.noName')
  const parts = name.split(/\s+/)
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''
  return `${first}${last}`.toUpperCase()
})

const items = computed<MenuItem[]>(() => [
  {
    id: 'identity',
    label: auth.user?.email ?? '',
    disabled: true,
    group: t('shell.userMenu.signedIn'),
  },
  {
    id: 'profile',
    label: t('nav.items.profile.label'),
    icon: 'info',
    group: t('shell.userMenu.account'),
  },
  {
    id: 'signout',
    label: t('common.actions.signOut'),
    icon: 'external',
    group: t('shell.userMenu.account'),
    destructive: true,
  },
])

/**
 * Sign-out does not wait for the revoke. `auth.logout()` ends the session
 * locally before its first await, so by the next statement there is nothing
 * left to protect; awaiting the server's answer would only keep this screen up
 * for as long as a slow or hung revoke takes — on a shared device, for the
 * next person to read (CLAUDE.md lesson 11).
 *
 * `replace`, not `push`: the previous person's URL (a record id, a `?q=`
 * search term) must not stay one Back press away for whoever sits down next.
 */
function onSelect(item: MenuItem): void {
  if (item.id === 'profile') {
    void router.push({ name: 'profile' })
    return
  }
  if (item.id === 'signout') {
    void auth.logout()
    void router.replace({ name: 'login' })
  }
}
</script>

<template>
  <EDropdownMenu :items="items" :label="$t('shell.userMenu.menuLabel')" @select="onSelect">
    <template #trigger>
      <EButton
        variant="ghost"
        size="sm"
        :aria-label="$t('shell.userMenu.trigger', { name: auth.displayName })"
      >
        <template #icon><AppIcon name="user" :size="15" /></template>
        <span class="user-menu__initials">{{ initials }}</span>
      </EButton>
    </template>
  </EDropdownMenu>
</template>

<style>
.user-menu__initials {
  font-variant-numeric: tabular-nums;
  letter-spacing: var(--erpx-tracking-wide);
}
</style>
